import BulkUploadHeader from '../components/BulkUploadHeader/BulkUploadHeader';
import { UPLOAD_CATALOG_STEPS } from './constants';
import { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { uploadCatalog } from 'services/bulkUpload/uploadCatalog.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import {
  UploadFormOption,
  UploadFormOptionCheckbox,
  UploadFormOptionDropdown
} from '../components/UploadForm/UploadForm.types';
import { getCountriesOpenForSelling } from 'services/countriesList';
import UploadForm from '../components/UploadForm/UploadForm';
import { ICountryExchangeDetail } from 'types/countries';
import {
  IUploadInfo,
  templateUploadedInfo,
  templateWithErrorsInfo,
  uploadTemplateInfo,
  uploadTemplateInfoWithErrors
} from './content/uploadCatalogInfo';
import { SummarySubmitSection } from './components/SummarySubmitSection/SummarySubmitSection';
import { IUploadResults } from 'types/bulkUpload';
import './UploadCatalog.scss';

export default function UploadCatalogModule() {
  const [currentStep, setCurrentStep] = useState(0);
  const [replaceCatalog, setReplaceCatalog] = useState(true);
  const [disableReplaceCatalog, setDisableReplaceCatalog] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<ICountryExchangeDetail | undefined>();
  const [countries, setCountries] = useState<ICountryExchangeDetail[]>([]);
  const [uploadResults, setUploadResults] = useState<IUploadResults | undefined>();
  const [errors, setErrors] = useState<string[]>([]);
  const [errorTemplateURL, setErrorTemplateURL] = useState<string | undefined>();
  const [goBackText, setGoBackText] = useState('');
  
  const { push } = useHistory();

  const dropdownOptions = useMemo<UploadFormOptionDropdown[]>(
    () => [
      {
        label: 'Pais*',
        options: countries.map(country => ({
          label: country.country || '',
          value: country?.id?.toString() || '',
          handleChange: () => setSelectedCountry(country)
        })),
        type: 'dropdown',
        defaultOption: {
          label: selectedCountry?.country || 'Selecciona el país',
          value: selectedCountry?.id?.toString() || '',
          isFilled: !!selectedCountry
        }
      }
    ],
    [countries, selectedCountry]
  );

  const checkboxOptions = useMemo<UploadFormOptionCheckbox[]>(
    () => [
      {
        label: '¿Qué quieres hacer con el catálogo?',
        type: 'checkbox',
        options: [
          {
            label: 'Solo actualizar información',
            checked: !replaceCatalog,
            handleChange: () => setReplaceCatalog(false),
            disabled: disableReplaceCatalog
          },
          {
            label: 'Reemplazar catálogo',
            checked: replaceCatalog,
            handleChange: () => setReplaceCatalog(true),
            disabled: disableReplaceCatalog
          }
        ]
      }
    ],
    [disableReplaceCatalog, replaceCatalog]
  );

  const UploadFormOptions = useMemo<UploadFormOption[]>(
    () => [...dropdownOptions, ...checkboxOptions],
    [dropdownOptions, checkboxOptions]
  );

  const handleSubmitUploadForm = async (file: File) => {
    try {
      if (!selectedCountry?.id) throw new SyntaxError('No se ha seleccionado un país');

      const uploadCatalogResponse = await uploadCatalog(file, replaceCatalog, selectedCountry?.id);
      setErrors(uploadCatalogResponse?.errors || []);
      setUploadResults(uploadCatalogResponse);
      setErrorTemplateURL(uploadCatalogResponse?.errorFileUrl);
      setCurrentStep(curr => curr + 1);
    } catch (error) {
      const errorMessage =
        error instanceof SyntaxError ? error.message : 'Error al subir el catálogo, por favor, inténtelo de nuevo';
      displayErrorNotification({
        customJSXMessage: <>{errorMessage}</>,
        duration: 5000
      });
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      push('/bulk-upload/type-of-charge');
    } else {
      setCurrentStep(curr => curr - 1);
    }
  };

  const summaryUploadInfo = useMemo<IUploadInfo>(() => {
    const updatedRecords = uploadResults?.updatedRecords || 0;
    const totalRecords = uploadResults?.totalRecords || 0;
    if (errors && errors.length > 0) {
      return templateWithErrorsInfo(updatedRecords, totalRecords);
    }
    return templateUploadedInfo(updatedRecords, totalRecords);
  }, [errors, uploadResults]);

  const uploadInfo = useMemo<IUploadInfo>(() => {
    if (errors && errors.length > 0) {
      return uploadTemplateInfoWithErrors(errorTemplateURL || '');
    }
    return uploadTemplateInfo;
  }, [errors, errorTemplateURL]);

  const handleReturnToUpload = () => {
    setCurrentStep(curr => curr - 1);
    setReplaceCatalog(false);
    setDisableReplaceCatalog(true);
  };

  const renderComponentInStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <UploadForm
            key={`step-${step}`}
            options={UploadFormOptions}
            onSubmit={handleSubmitUploadForm}
            backLink="/bulk-upload/type-of-charge"
            confirmationModal={replaceCatalog}
            fileInputIsDisabled={!selectedCountry}
            uploadInfo={uploadInfo}
            customClassName="uploadCatalogForm"
          />
        );
      case 1:
        return (
          <UploadForm
            key={`step-${step}`}
            onSubmit={handleSubmitUploadForm}
            backLink="/bulk-upload/type-of-charge"
            confirmationModal={replaceCatalog}
            fileInputIsDisabled={!selectedCountry}
            hideAlert
            hideFileInput
            uploadInfo={summaryUploadInfo}
            customSubmitSection={
              <SummarySubmitSection
                areErrors={errors && errors.length > 0}
                errorTemplateURL={errorTemplateURL}
                handleReturnToUpload={handleReturnToUpload}
              />
            }
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    getCountriesOpenForSelling().then(countries => {
      setCountries(countries);
    });
  }, []);

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setGoBackText('Volver a Cargue');
        break;
      case 1:
        setGoBackText(`Volver a ${UPLOAD_CATALOG_STEPS[0]}`);
        break;
      default:
        setGoBackText('');
    }
  }, [currentStep]);

  return (
    <main className="upload-catalog-container">
      <BulkUploadHeader steps={UPLOAD_CATALOG_STEPS} currentStep={currentStep} handleBack={handleBack} goBackText={goBackText} />
      {renderComponentInStep(currentStep)}
    </main>
  );
}
